import { useEffect, useMemo } from 'react';
import { SocketEventType } from 'types/socket';
import {
  noteListenersRegistered,
  noteListenersRemoved,
  noteSocketEvent,
  noteSocketHandlerError,
  registerListenerReviver,
} from 'utils/socket-health-monitor';
import { useSignalR } from './useSignalR';

/**
 * Hook for registering socket event listeners.
 *
 * @param connection - The SignalR HubConnection or null.
 * @param events - An array of event names to register.
 * @param onSocketNotif - Callback function that is invoked for each event.
 * @param monitorScope - תווית לניטור בריאות הסוקט בלבד ('global' / 'chat').
 */
export function useSocketListeners(
  events: Array<SocketEventType>,
  onSocketNotif: (type: SocketEventType, message: string | null | any) => void,
  monitorScope: string = 'unknown',
) {
  const { connection } = useSignalR();
  // Memoize the handlers to ensure their references remain stable.
  const socketHandlers = useMemo(() => {
    const handlers: Record<string, (message: any) => void> = {};
    events.forEach((eventName) => {
      handlers[eventName] = (message: any) => {
        //* ניטור בלבד — רישום קבלת האירוע לפני עיבודו.
        noteSocketEvent(eventName, monitorScope);
        try {
          // אם ההודעה היא אובייקט, נשלח אותה ישירות, אחרת נשלח אותה כמחרוזת
          onSocketNotif(eventName, typeof message === 'string' ? message : message);
        } catch (error) {
          //* אירוע התקבל אך עיבודו נכשל — נרשם ליומן והשגיאה ממשיכה כרגיל.
          noteSocketHandlerError(eventName, monitorScope, error);
          throw error;
        }
      };
    });
    return handlers;
  }, [events, onSocketNotif, monitorScope]);

  useEffect(() => {
    if (!connection) return;

    //* off לפני on — רישום אידמפוטנטי, כך שגם קריאת החייאה מה-watchdog לא תכפיל handlers
    const bind = () => {
      events.forEach((eventName) => {
        connection.off(eventName, socketHandlers[eventName]);
        connection.on(eventName, socketHandlers[eventName]);
      });
      noteListenersRegistered(monitorScope, events);
    };

    bind();

    //* ריפוי עצמי (14.10): הסקופים מתים בנפרד בפרודקשן (ייצוא קייסי — chat:0 עם
    //* סוקט מחובר). ה-watchdog קורא ל-bind כשמונה המאזינים של הסקופ צונח לאפס
    //* בזמן שהרכיב עדיין חי — במקום שהמשתמש יגלה הודעות חסרות וירענן ידנית.
    const unregisterReviver = registerListenerReviver(monitorScope, bind);

    // Cleanup: Unregister the handlers when the component unmounts or dependencies change.
    return () => {
      unregisterReviver();
      events.forEach((eventName) => {
        connection.off(eventName, socketHandlers[eventName]);
      });
      noteListenersRemoved(monitorScope, events);
    };
  }, [connection, events, socketHandlers, monitorScope]);
}
